import React from "react";

/**
 * Primitivas compactas para la pantalla de Meta Ads.
 *
 * No usa Card/CardHeader del tema a propósito: ese Card trae marginTop y marginBottom de 30px
 * y el CardHeader de color flota con marginTop:-20px y 15px de padding. Con cinco bloques
 * apilados eso se come la pantalla entera y hay que scrollear para ver una tabla de 5 filas.
 *
 * Acá los bloques son cajas de 1px con 10px de separación, y la densidad de las tablas se
 * resuelve con CSS inyectado una sola vez en vez de repetir estilos en cada celda.
 */

const CSS = `
.ma-root { font-size: 13px; }

/* Tablas densas: el default de MUI usa 16px de padding vertical por celda. */
.ma-root table { width: 100%; border-collapse: collapse; }
.ma-root th, .ma-root td { padding: 5px 10px !important; border-bottom: 1px solid #EEF1F6; height: auto !important; }
.ma-root thead th {
  font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #7A8699; background: #F7F9FC; border-bottom: 1px solid #E2E8F0;
  position: sticky; top: 0; z-index: 2; white-space: nowrap;
}
.ma-root tbody td { font-size: 12.5px; color: #24303F; }
.ma-root tbody tr:hover td { background: #FAFCFF; }

/* Inputs compactos: los TextField del tema tienen mucho aire vertical. */
.ma-root .MuiFormControl-root { margin: 0; }
.ma-root .MuiInputBase-input { font-size: 13px; padding-top: 5px; padding-bottom: 5px; }
.ma-root .MuiInputLabel-root { font-size: 12px; }
.ma-root .MuiSelect-select { font-size: 13px; padding-top: 5px; padding-bottom: 5px; }
.ma-root .MuiOutlinedInput-multiline { padding: 8px 10px; }

/* Botones del tema: 12px de padding vertical y margen propio. */
.ma-root .ma-btn {
  font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
  padding: 5px 11px; border-radius: 5px; border: 1px solid #D3DAE6; background: #fff;
  color: #46536B; cursor: pointer; line-height: 1.5;
}
.ma-root .ma-btn:hover { background: #F2F6FC; }
.ma-root .ma-btn[data-on="1"] { background: #2B6CB0; border-color: #2B6CB0; color: #fff; }
.ma-root .ma-btn[data-tone="ok"] { background: #16A34A; border-color: #16A34A; color: #fff; }
.ma-root .ma-btn[data-tone="no"] { background: #fff; border-color: #F0B4B4; color: #B42318; }
.ma-root .ma-btn[data-tone="pri"] { background: #7B1FA2; border-color: #7B1FA2; color: #fff; }
.ma-root .ma-btn:disabled { opacity: .45; cursor: not-allowed; }

/* Fila de indicadores. Se ajusta sola: los mismos bloques a 3 o a 9 por línea. */
.ma-root .ma-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(112px, 1fr)); gap: 1px; background: #E9EEF6; }
.ma-root .ma-kpi { background: #fff; padding: 7px 10px; }
.ma-root .ma-kpi-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #7A8699; white-space: nowrap; }
.ma-root .ma-kpi-val { font-size: 17px; font-weight: 700; color: #24303F; line-height: 1.3; letter-spacing: -.01em; }
.ma-root .ma-kpi-sub { font-size: 10.5px; color: #8A94A6; }

/*
 * Barra dentro de una celda. La magnitud es UNA serie, así que va en un solo tono; el color
 * no codifica nada y por eso no cambia entre filas. El valor siempre va también como número:
 * la barra es para comparar de un vistazo, no para leer.
 */
.ma-root .ma-bar { height: 6px; border-radius: 0 3px 3px 0; background: #2B6CB0; min-width: 1px; }
.ma-root .ma-bar-bg { background: #EEF2F8; border-radius: 3px; overflow: hidden; }
`;

let inyectado = false;
function inyectarCss() {
  if (inyectado || typeof document === "undefined") return;
  const s = document.createElement("style");
  s.setAttribute("data-ma", "1");
  s.appendChild(document.createTextNode(CSS));
  document.head.appendChild(s);
  inyectado = true;
}

/** Contenedor de la pantalla. Sube el contenido: el layout deja mucho aire arriba. */
export function Root({ children }) {
  inyectarCss();
  return <div className="ma-root" style={{ marginTop: -18 }}>{children}</div>;
}

/** Barra horizontal de controles. Todo en una línea, envuelve en pantallas chicas. */
export function Bar({ children, style }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10, ...style }}>
      {children}
    </div>
  );
}

export function Btn({ children, on, tone, ...rest }) {
  return (
    <button className="ma-btn" data-on={on ? "1" : undefined} data-tone={tone} {...rest}>
      {children}
    </button>
  );
}

/** Bloque de contenido. Reemplaza a Card: borde de 1px y 10px de separación. */
export function Panel({ title, right, children, pad = 10, style }) {
  return (
    <div
      style={{
        background: "#fff", border: "1px solid #E3E8F0", borderRadius: 8,
        marginBottom: 10, overflow: "hidden", ...style,
      }}
    >
      {(title || right) && (
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "6px 10px", borderBottom: "1px solid #EEF1F6", background: "#FBFCFE",
            minHeight: 30,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#5A6880" }}>
            {title}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>{right}</div>
        </div>
      )}
      <div style={{ padding: pad }}>{children}</div>
    </div>
  );
}

/** Franja de una línea, para un dato que no merece un bloque entero. */
export function Strip({ tone = "info", children, style }) {
  const c = {
    info:  { bg: "#F4F8FF", bd: "#2B6CB0", fg: "#1B3A5C" },
    warn:  { bg: "#FFFBEB", bd: "#CA8A04", fg: "#7A5B05" },
    error: { bg: "#FEF3F2", bd: "#D92D20", fg: "#8E2018" },
    ok:    { bg: "#F0FDF6", bd: "#16A34A", fg: "#10643A" },
  }[tone];

  return (
    <div
      style={{
        background: c.bg, borderLeft: `3px solid ${c.bd}`, color: c.fg,
        padding: "6px 10px", borderRadius: 4, fontSize: 12.5, marginBottom: 10,
        lineHeight: 1.45, ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Etiqueta chica sobre un control, para no gastar la altura de un InputLabel del tema. */
export function Lbl({ children }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "#7A8699", marginBottom: 2 }}>
      {children}
    </div>
  );
}

/**
 * Un indicador. El valor manda y la etiqueta es secundaria: al revés hay que leer la fila
 * entera para encontrar el número.
 *
 * `tone` se reserva para estado (bien / atención / mal) y no se usa para distinguir un
 * indicador de otro: si todo tiene color, el color deja de avisar nada.
 */
export function Kpi({ label, value, sub, tone }) {
  const c = { ok: "#16A34A", warn: "#CA8A04", bad: "#D92D20" }[tone];
  return (
    <div className="ma-kpi">
      <div className="ma-kpi-lbl">{label}</div>
      <div className="ma-kpi-val" style={c ? { color: c } : undefined}>{value}</div>
      {sub != null && <div className="ma-kpi-sub">{sub}</div>}
    </div>
  );
}

export function Kpis({ children }) {
  return <div className="ma-kpis">{children}</div>;
}

export const money = (n) => (n == null ? "—" : "$" + Math.round(n).toLocaleString("es-AR"));

/**
 * Igual que `money` pero para costos unitarios, que suelen ser chicos.
 *
 * Redondear a entero mata justo el número que hay que mirar: un CPC de $0.09 se muestra como
 * "$0" y un CPM de $1.87 como "$2". Debajo de $10 van dos decimales; de ahí para arriba el
 * centavo no cambia ninguna decisión y sólo mete ruido en la columna.
 */
export const precio = (n) => {
  if (n == null) return "—";
  const dec = Math.abs(n) < 10 ? 2 : 0;
  return "$" + Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

/** Enteros con separador de miles: 209116 impresiones no se leen sin él. */
export const num = (n) => (n == null ? "—" : Math.round(n).toLocaleString("es-AR"));

export const pct = (n, dec = 2) => (n == null ? "—" : Number(n).toFixed(dec) + "%");

/**
 * Un delta contra una referencia. Devuelve el texto y el tono, pero NO decide si subir es
 * bueno: en ROAS sí y en CPA no, así que el signo se lo pasa quien llama.
 */
export function Delta({ valor, mejorSiSube = true, dec = 0 }) {
  if (valor == null) return <span style={{ color: "#A0AABA" }}>—</span>;
  const bueno = mejorSiSube ? valor > 0 : valor < 0;
  const neutro = Math.abs(valor) < 5;
  return (
    <span style={{ color: neutro ? "#7A8699" : bueno ? "#16A34A" : "#D92D20", fontWeight: 700 }}>
      {valor > 0 ? "+" : ""}{Number(valor).toFixed(dec)}%
    </span>
  );
}
